import type { Role } from '@plato/data/prisma';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@plato/ui/components/select';
import { User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getRoleLabel } from '../utils/get-role-label.ts';

type RoleSelectProps = {
    value: Role | undefined;
    onChange: (value: Role | undefined) => void;
    options: Role[];
    isLoading?: boolean;
};

const allRolesOption = '__ALL_ROLES__';

export function RoleSelect({ isLoading, value, onChange, options }: RoleSelectProps) {
    const { t } = useTranslation('team');

    const roles = value && !options.includes(value) ? [...options, value] : options;

    const items = [
        { value: allRolesOption, label: t('all_roles') },
        ...roles.map((role) => ({ value: role, label: getRoleLabel(t, role) ?? '' })),
    ];

    return (
        <Select
            title={t('role_label')}
            items={items}
            value={value ?? allRolesOption}
            onValueChange={(next) => {
                // The "all roles" sentinel matches no enum value → undefined.
                onChange(roles.find((role) => role === next));
            }}
        >
            <SelectTrigger disabled={!isLoading && roles.length < 2} aria-label={t('role_label')}>
                <User className="size-4 shrink-0 text-content-weaker" />
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectList>
                    {(item: SelectOption) => <SelectItem key={item.value} value={item} />}
                </SelectList>
            </SelectContent>
        </Select>
    );
}
